import { formatPrice } from '@polaris/core/utils'
import { useAtomValue } from 'jotai'
import Image from 'next/image'
import { useMemo } from 'react'
import { WalletIcon } from '@/src/components/accounts/wallet-icon'
import {
  additionalFeesAtom,
  additionalFeesImageAtom,
  fromWalletAtom,
  gasStepAtom,
  isUserRejectionErrorAtom,
  routeAtom,
  shouldExecuteNamadaUnshieldAtom,
  shouldTransferFundsToAutopilotWalletAtom,
} from '@/src/stores/route-machine/atoms'
import { BasicStep, BasicStepTitle } from './basic-step'

const Title = () => {
  const gasStep = useAtomValue(gasStepAtom)
  const isFirstStepOfTwo = useAtomValue(
    shouldTransferFundsToAutopilotWalletAtom,
  )
  const isUserRejectionError = useAtomValue(isUserRejectionErrorAtom)
  const title = useMemo(() => {
    if (isUserRejectionError) return 'Approval rejected'
    if (gasStep.isSigning) return 'Pre-pay fees'
    if (gasStep.isLoading) return 'Paying fees'

    return 'Pre-pay fees'
  }, [gasStep.isSigning, gasStep.isLoading, isUserRejectionError])

  return (
    <div className='flex items-center gap-1 py-1 w-full justify-between'>
      <BasicStepTitle title={title} isError={isUserRejectionError} />
      {isFirstStepOfTwo && (
        <span className='text-tertiary font-normal'>1/2</span>
      )}
    </div>
  )
}

export const GasStationStep = () => {
  const gasStep = useAtomValue(gasStepAtom)
  const isUserRejectionError = useAtomValue(isUserRejectionErrorAtom)
  const fromWalletDetails = useAtomValue(fromWalletAtom)
  const additionalFees = useAtomValue(additionalFeesAtom)
  const additionalFeesImage = useAtomValue(additionalFeesImageAtom)
  const isFirstStepOfTwo = useAtomValue(
    shouldTransferFundsToAutopilotWalletAtom,
  )
  const isNamadaPrivacyRoute = useAtomValue(shouldExecuteNamadaUnshieldAtom)
  const route = useAtomValue(routeAtom)
  const isWarning = gasStep.error && isUserRejectionError
  const totalFees = route?.FeeRequirements?.gas_station?.total_usd_to_fund ?? 0

  const broadcasting = gasStep.isLoading && !gasStep.isSigning

  return (
    <BasicStep
      title={<Title />}
      isActive={gasStep.isActive}
      isLoading={gasStep.isLoading}
      isError={gasStep.isError}
      isWarning={isWarning}
      isWaiting={gasStep.isSigning}
      isSuccess={gasStep.isSuccess}
    >
      {!broadcasting ? (
        <div className='flex flex-col text-left gap-2'>
          <h6 className='flex items-center gap-2 font-semibold text-primary text-xl'>
            {isNamadaPrivacyRoute ? (
              <span>Approving</span>
            ) : (
              <>
                <span>Approve in</span>
                {!!fromWalletDetails?.logoUrl && (
                  <WalletIcon
                    logoUrl={fromWalletDetails.logoUrl}
                    walletName={fromWalletDetails.displayName}
                    size={24}
                    className='size-6'
                  />
                )}
                <span>{fromWalletDetails?.displayName}</span>
              </>
            )}
          </h6>
          <span className='flex items-center gap-1 text-secondary text-sm'>
            {additionalFeesImage ? (
              <Image
                src={additionalFeesImage}
                alt={''}
                width={24}
                height={24}
                className='size-6'
                unoptimized
              />
            ) : null}
            {totalFees > 0
              ? // totalFees are the newly added fee calculator tracker that specifically tracks gas station fees plus input buffer.
                // If this is populated, we want to use this.
                `${formatPrice(totalFees)} to cover fees for this trade.`
              : // Otherwise, we use the old fee calculator that tracks all fees. This was the bug previously, where the dialog box
                // was displaying all fees on the gas station step, implying that the user needed to pay this amount to the gas station
                // which was untrue.
                `${formatPrice(additionalFees ?? 0)} to cover all fees for this trade.`}
          </span>
        </div>
      ) : (
        <span className='flex items-center gap-1 text-secondary text-sm'>
          Please wait a moment{' '}
          {isFirstStepOfTwo && !isNamadaPrivacyRoute
            ? 'for the next approval '
            : ''}
          to start your trade
        </span>
      )}
    </BasicStep>
  )
}
